/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

'use client'

import { SearchIcon, SlidersHorizontalIcon } from 'lucide-react'
import type { ChangeEvent, ComponentProps, ReactNode } from 'react'
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react'
import type { RegisterOptions } from 'react-hook-form'
import { useFormContext } from 'react-hook-form'

import { Button } from '@/components/ui/button'
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible'
import { Field, FieldError } from '@/components/ui/field'
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group'
import { Spinner } from '@/components/ui/spinner'
import { cn } from '@/lib/utils'

export interface SearchExpandProps extends ComponentProps<'div'> {
  children?: ReactNode
  debounceTime?: number
  fieldProps?: ComponentProps<typeof Field>
  /** When true, disables search input, clear, filter toggle, and keeps the collapsible closed. */
  filterControlsDisabled?: boolean
  placeholder?: string
  inputProps?: ComponentProps<typeof InputGroupInput>
  leftBarChildren?: ReactNode
  loading?: boolean
  minChars?: number
  name: string
  onSearchChange?: (value: string) => void
  rightBarChildren?: ReactNode
  validation?: RegisterOptions
  /** English default for clear control; override when wiring i18n at the app layer. */
  clearSearchLabel?: string
  /** English default for filter toggle; override when wiring i18n at the app layer. */
  toggleFiltersLabel?: string
}

export const SearchExpand = forwardRef<HTMLInputElement, SearchExpandProps>((props, forwardedRef) => {
  const {
    debounceTime = 500,
    filterControlsDisabled = false,
    loading,
    minChars = 3,
    onSearchChange,
    clearSearchLabel = 'Clear search',
    toggleFiltersLabel = 'Toggle filters',
    className,
    children,
    fieldProps,
    inputProps,
    leftBarChildren,
    name,
    placeholder,
    rightBarChildren,
    validation,
    ...rest
  } = props
  const formMethods = useFormContext()
  const {
    formState: { errors },
    register,
  } = formMethods

  const [filtersExpanded, setFiltersExpanded] = useState(false)
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const { ref: registerRef, ...workRegister } = register(name, validation)

  const setRefs = useCallback(
    (node: HTMLInputElement | null) => {
      registerRef(node)
      if (typeof forwardedRef === 'function') {
        forwardedRef(node)
      } else if (forwardedRef) {
        forwardedRef.current = node
      }
    },
    [forwardedRef, registerRef]
  )

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current)
    }

    const shouldNotify = value.length >= minChars || value === ''
    debounceTimeoutRef.current = setTimeout(() => {
      if (shouldNotify) {
        onSearchChange?.(value)
      }
    }, debounceTime)

    workRegister.onChange(event)
  }

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (filterControlsDisabled) {
      setFiltersExpanded(false)
    }
  }, [filterControlsDisabled])

  const toggleFilters = () => {
    setFiltersExpanded(!filtersExpanded)
  }

  return (
    <div className={cn('flex w-full flex-col gap-0 pb-2', filtersExpanded && 'gap-2', className)} {...rest}>
      <div className="flex w-full items-start gap-2">
        {leftBarChildren}

        <Field
          {...fieldProps}
          className={cn('min-w-0 flex-1', fieldProps?.className)}
          data-invalid={Boolean(errors[name])}
        >
          <InputGroup className="h-10">
            <InputGroupInput
              aria-invalid={Boolean(errors[name])}
              aria-label={placeholder}
              className={inputProps?.className}
              placeholder={placeholder}
              ref={setRefs}
              suppressHydrationWarning
              type="search"
              {...workRegister}
              {...inputProps}
              disabled={filterControlsDisabled || inputProps?.disabled}
              onChange={handleChange}
            />
            <InputGroupAddon align="inline-end">
              <div className="flex items-center gap-1">
                {loading ? (
                  <Spinner className="size-5 text-primary" />
                ) : (
                  <SearchIcon className="size-5 text-muted-foreground" />
                )}
              </div>
            </InputGroupAddon>
          </InputGroup>
          <FieldError>{errors[name]?.message != null ? String(errors[name]?.message) : null}</FieldError>
        </Field>

        {children ? (
          <Button
            aria-label={toggleFiltersLabel}
            disabled={filterControlsDisabled}
            onClick={toggleFilters}
            size="icon"
            type="button"
            variant={filtersExpanded ? 'outline' : 'default'}
          >
            <SlidersHorizontalIcon className="size-5" />
          </Button>
        ) : null}

        {rightBarChildren}
      </div>

      {children ? (
        <Collapsible
          onOpenChange={(open) => {
            if (!filterControlsDisabled) {
              setFiltersExpanded(open)
            }
          }}
          open={filtersExpanded}
        >
          <CollapsibleContent>{children}</CollapsibleContent>
        </Collapsible>
      ) : null}
    </div>
  )
})

SearchExpand.displayName = 'SearchExpand'
